import axios from 'axios';
import { useLayoutEffect } from 'react';
import { toast } from 'sonner';

import useAuth from '../guards/authGuard/UseAuth';
import { IS_DEV } from './config';
import { getPortalType } from 'src/guards/jwt/utils';
import {ErrorCode} from "../requests/utils";

const axiosServices = axios.create();

const excludedEndpoints = ['accept-voucher', 'confirm-paid-invoice'];
const toastExcludedEndpoints = ['email/login','2fa/use-backup-code', '2fa/verify'];

let refreshTokenPromise = null;

// interceptor for http
const AxiosInterceptor = ({ children }) => {
  const { logout, refreshToken, user, isAuthenticated } = useAuth();

  useLayoutEffect(() => {
    // set jwt token to header
    const requestInterceptor = axiosServices.interceptors.request.use(
      (config) => {
        const portalType = getPortalType(window.location.pathname);
        const token = localStorage.getItem(`${portalType?.toLowerCase()}-token`);

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        if (IS_DEV) {
          config.headers['ngrok-skip-browser-warning'] = 'true';
        }

        return config;
      },
      async (error) => {
        return Promise.reject(error);
      },
    );

    // refresh token if 401 status
    const responseInterceptor = axiosServices.interceptors.response.use(
      (res) => {
        return res;
      },
      async (err) => {
        const originalConfig = err.config || {};

        if (err.response) {
          const errorCode = err.response.data.errorCode;
          const isRefreshTokenRequest = originalConfig?.url?.includes('/auth/refresh-token');
          const portalType = user?.roles?.[0] || getPortalType(window.location.pathname);

          // Access Token was expired
          if (err.response.status === 401 
              && !originalConfig._retry
              && isAuthenticated 
              && errorCode === ErrorCode.JWT_EXPIRED
              && !isRefreshTokenRequest
          ) {
            originalConfig._retry = true;

            try {
              if (!refreshTokenPromise) {
                refreshTokenPromise = Promise.resolve(refreshToken()).finally(() => {
                  refreshTokenPromise = null;
                });
              }

              return refreshTokenPromise.then(() => axiosServices(originalConfig));
            } catch (_error) {
              logout(portalType);

              return Promise.reject(_error);
            }
          } else if (err.response.status === 403 && err.response.data) {
            if (!IS_DEV && errorCode !== ErrorCode.TEMPORARILY_BLOCKED) {
              logout(portalType);
            }

            return Promise.reject(err.response.data);
          } else if (
            err.response.status === 409 &&
            excludedEndpoints.some((e) => err.request.responseURL.includes(e))
          ) {
            err.config._errorToasted = true;
          } else {
            // show toast with error message
            if (!err.config._errorToasted && errorCode !== ErrorCode.JWT_EXPIRED
                && !toastExcludedEndpoints.some((e) => err.request.responseURL.includes(e))) {
              err.config._errorToasted = true;
              toast.error(
                err.response.data.error ||
                err.response.data.message ||
                err.response.data.status ||
                'Unknown error',
              );
            }
          }
        }

        return Promise.reject(err);
      },
    );

    return () => {
      axiosServices.interceptors.request.eject(requestInterceptor);
      axiosServices.interceptors.response.eject(responseInterceptor);
    };
  }, [isAuthenticated, logout, refreshToken, user]);

  return children;
};

export default axiosServices;
export { AxiosInterceptor };